import { InsightsSection, LiveSection, TargetSection } from '@/components/dashboard-insights';
import { Fragment, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { readDashboardLayout, type DashboardPeriodDto, type DashboardSection } from '@ecom/contracts';
import { DashboardCustomize } from '@/components/dashboard-customize';
import { columnsOf, rowsOf } from '@/lib/dashboard-layout';
import { useOverviewStore, type OverviewPeriod } from '@/stores/overview.store';
import { usePreferencesStore } from '@/stores/preferences.store';
import { changeFrom } from '@/lib/compare';
import { useUiStore } from '@/i18n';
import { useT, useStatusLabel } from '@/i18n/use-t';
import type { MessageKey } from '@/i18n/messages';
import type { FollowUpDto, FunnelReportDto } from '@ecom/contracts';
import { age, count, dateTime, longDay, money, moneyOrDash, partOfDay, share, signedPercent } from '@/lib/format';
import { statusTone } from '@/lib/order-status';
import { can } from '@/lib/session';
import { useAuthStore } from '@/stores/auth.store';
import {
  AlertIcon,
  BoxIcon,
  CartIcon,
  ChartIcon,
  CheckIcon,
  CoinsIcon,
  ReceiptIcon,
  ReturnIcon,
  SlidersIcon,
  TruckIcon,
  UsersIcon,
} from '@/components/icons';
import { Page, Section, Segmented, Table, Td, Th, Tr } from '@/components/page';
import { TrendChart } from '@/components/trend-chart';
import {
  Alert,
  Button,
  ButtonLink,
  EmptyState,
  MetricCard,
  MetricSkeleton,
  Pill,
  ProgressRow,
  Skeleton,
  cx,
} from '@/components/ui';

/**
 * The overview.
 *
 * Answers one question in under ten seconds: how is the store doing, and what
 * needs me right now? One period at the top scopes every figure that has one
 * — the cards and their change from the period before, the day-by-day chart,
 * the best sellers — so they always agree (docs/07-Admin/Dashboard.md). Then
 * the "now": the orders waiting longest, and what is about to run out.
 *
 * Each panel shows its own skeleton on the first read, so a slow query in one
 * never blanks the others; a new period keeps the last picture, faded, until
 * the new one lands.
 *
 * Each person arranges their own (ADR-109): the sections in their order, any
 * of them hidden, under Customize — where they also choose the summary by
 * email. Whoever never arranged sees the default.
 */
const PERIODS: { days: OverviewPeriod; label: MessageKey }[] = [
  { days: 7, label: 'last7Days' },
  { days: 30, label: 'last30Days' },
  { days: 90, label: 'last90Days' },
];

const GREETINGS: Record<ReturnType<typeof partOfDay>, MessageKey> = {
  morning: 'greetingMorning',
  afternoon: 'greetingAfternoon',
  evening: 'greetingEvening',
};

export function OverviewPage() {
  const { days, summary, trend, topSellers, queue, lowStock, toCall, recentOrders, visits, myFollowUps, loading, refreshing, error, load } =
    useOverviewStore();
  const user = useAuthStore((state) => state.user);
  const preferences = usePreferencesStore((state) => state.preferences);
  const loadPreferences = usePreferencesStore((state) => state.load);
  const [customizing, setCustomizing] = useState(false);
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const statusLabel = useStatusLabel();
  const navigate = useNavigate();

  // Product names are resolved server-side, so a language change is a refetch,
  // not just a re-render.
  // Their own sections first, so a hidden one is never asked for.
  useEffect(() => {
    void loadPreferences().then(() => load());
  }, [load, loadPreferences, locale]);

  const viewAll = (to: string) => (
    <ButtonLink to={to} variant="ghost" size="sm">
      {t('viewAll')}
    </ButtonLink>
  );

  /** How a period figure moved since the period before, as a card's second line. */
  const versus = (key: keyof DashboardPeriodDto) => {
    if (!summary?.previous) return undefined;
    const change = changeFrom(summary[key], summary.previous[key]);
    return {
      direction: change.direction,
      text:
        change.percent === null
          ? change.direction === 'flat'
            ? t('sameAsPrevious')
            : t('newVsPrevious')
          : change.percent === 0
            ? t('sameAsPrevious')
            : t('vsPrevious', { change: signedPercent(change.percent, locale) }),
    };
  };

  const periodLabel = t(PERIODS.find((period) => period.days === days)?.label ?? 'last30Days');
  const topUnits = Math.max(1, ...topSellers.map((product) => product.unitsSold));
  const rows = rowsOf(readDashboardLayout(preferences?.dashboard), (permission) => can(user, permission));

  /** Each section as it renders, by its id — laid out in this person's order below. */
  const render: Record<DashboardSection, () => ReactNode> = {
    metrics: () =>
      loading && !summary ? (
        <MetricSkeleton />
      ) : summary ? (
        <div className={cx('grid gap-4 transition-opacity sm:grid-cols-2 xl:grid-cols-4', refreshing && 'opacity-60')}>
          <MetricCard
            label={t('revenue')}
            value={money(summary.revenue, locale)}
            trend={versus('revenue')}
            hint={periodLabel}
            tone="mint"
            icon={<CoinsIcon />}
            featured
          />
          <MetricCard
            label={t('orderCount')}
            value={count(summary.orderCount, locale)}
            trend={versus('orderCount')}
            hint={periodLabel}
            tone="sky"
            icon={<CartIcon />}
          />
          <MetricCard
            label={t('averageOrderValue')}
            // Zero orders means no average exists — "—" rather than a fake zero.
            value={summary.orderCount === 0 ? '—' : moneyOrDash(summary.averageOrderValue, locale)}
            trend={summary.orderCount === 0 ? undefined : versus('averageOrderValue')}
            hint={periodLabel}
            tone="violet"
            icon={<ReceiptIcon />}
          />
          <MetricCard
            label={t('newCustomers')}
            value={count(summary.newCustomers, locale)}
            trend={versus('newCustomers')}
            hint={periodLabel}
            tone="violet"
            icon={<UsersIcon />}
          />

          <Link to="/orders?view=toFulfil" className="rounded-card focus-visible:outline-2">
            <MetricCard
              label={t('awaitingFulfilment')}
              value={count(summary.awaitingFulfilment, locale)}
              tone={summary.awaitingFulfilment > 0 ? 'amber' : 'mint'}
              icon={<TruckIcon />}
            />
          </Link>
          <Link to="/returns" className="rounded-card focus-visible:outline-2">
            <MetricCard label={t('openReturns')} value={count(summary.openReturns, locale)} tone="rose" icon={<ReturnIcon />} />
          </Link>
          <Link to="/inventory" className="rounded-card focus-visible:outline-2">
            <MetricCard
              label={t('lowStockCount')}
              value={count(summary.lowStockVariants, locale)}
              tone={summary.lowStockVariants > 0 ? 'rose' : 'mint'}
              icon={<BoxIcon />}
            />
          </Link>
          <MetricCard
            label={t('taxCollected')}
            value={money(summary.taxCollected, locale)}
            trend={versus('taxCollected')}
            hint={periodLabel}
            tone="amber"
            icon={<ReceiptIcon />}
          />
        </div>
      ) : null,
    salesTrend: () => (
      <Section title={t('salesTrend')} subtitle={t('salesTrendHint')} action={viewAll('/reports')}>
        {loading && trend.length === 0 ? (
          <Skeleton className="h-[220px]" />
        ) : (
          <TrendChart
            points={trend}
            locale={locale}
            label={`${t('salesTrend')} · ${periodLabel}`}
            ordersLabel={t('ordersWord')}
            emptyLabel={t('noSalesInPeriod')}
            dimmed={refreshing}
          />
        )}
      </Section>
    ),
    topSellers: () => (
      <Section title={t('topSellers')} subtitle={t('topSellersHint')}>
        {loading && topSellers.length === 0 ? (
          <div className="space-y-4">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-8" />
            ))}
          </div>
        ) : topSellers.length === 0 ? (
          <EmptyState title={t('noTopSellers')} icon={<ChartIcon />} />
        ) : (
          <ol className={cx('flex flex-col gap-4 transition-opacity', refreshing && 'opacity-50')}>
            {topSellers.slice(0, 6).map((product, index) => (
              <li key={product.sku} className="flex flex-col gap-1.5">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="flex min-w-0 items-baseline gap-2">
                    <span data-numeric className="w-4 shrink-0 text-xs text-ink-faint">
                      {count(index + 1, locale)}
                    </span>
                    {product.productId ? (
                      <Link to={`/products/${product.productId}`} className="truncate text-sm text-ink hover:underline">
                        {product.name}
                      </Link>
                    ) : (
                      <span className="truncate text-sm text-ink">{product.name}</span>
                    )}
                  </span>
                  <span data-numeric className="shrink-0 text-sm font-medium text-ink">
                    {money(product.revenue, locale)}
                  </span>
                </div>
                <div className="flex items-center gap-3 ps-6">
                  <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-sunken">
                    <span
                      className="block h-full rounded-full"
                      style={{ width: `${Math.max(3, (product.unitsSold / topUnits) * 100)}%`, backgroundColor: 'var(--chart-1)' }}
                    />
                  </span>
                  <span className="shrink-0 text-xs text-ink-muted">
                    {t('unitsSoldCount', { count: count(product.unitsSold, locale) })}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        )}
      </Section>
    ),
    actionQueue: () => (
      <Section
        title={t('actionQueue')}
        subtitle={t('queueOldestFirst')}
        action={viewAll('/orders?view=toFulfil')}
        flush
      >
        {loading && queue.length === 0 ? (
          <div className="space-y-3 border-t border-line p-5">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        ) : queue.length === 0 ? (
          <div className="border-t border-line">
            <EmptyState title={t('queueEmpty')} body={t('queueEmptyHint')} icon={<CheckIcon />} />
          </div>
        ) : (
          // Oldest first, from the server — so nothing rots at the bottom of the list.
          <ul className="flex flex-col border-t border-line">
            {queue.map((order) => (
              <li key={order.id} className="border-t border-line first:border-t-0">
                <Link
                  to={`/orders/${order.id}`}
                  className="flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-surface-sunken"
                >
                  <span data-numeric dir="ltr" className="text-sm font-medium text-brand-700">
                    {order.number}
                  </span>
                  <Pill tone={statusTone(order.status)}>{statusLabel(order.status, order.isPickup)}</Pill>
                  <span className="hidden min-w-0 truncate text-sm text-ink-muted sm:inline">
                    {order.shippingAddress.fullName}
                  </span>
                  <span className="ms-auto shrink-0 text-xs text-ink-muted">{age(order.placedAt ?? order.createdAt, locale)}</span>
                  <span data-numeric className="shrink-0 text-sm font-medium text-ink">
                    {money(order.totals.grandTotal, locale, order.totals.currency)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Section>
    ),
    lowStock: () => (
      <Section title={t('lowStock')} subtitle={t('lowStockHint')} action={viewAll('/inventory')}>
        {loading && lowStock.length === 0 ? (
          <div className="space-y-4">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-8" />
            ))}
          </div>
        ) : lowStock.length === 0 ? (
          <EmptyState title={t('lowStockEmpty')} icon={<CheckIcon />} />
        ) : (
          <div className="flex flex-col gap-5">
            {lowStock.map((item) => (
              <Link key={item.variantId} to={`/products/${item.productId}`} className="block rounded-control hover:opacity-80">
              <ProgressRow
                // The SKU tells apart the colours and sizes of one product.
                label={`${item.productName} · ${item.sku}`}
                value={item.stock}
                // A bar needs a ceiling; the worst row in view sets the scale
                // so the shortest bar is always the most urgent one.
                max={Math.max(...lowStock.map((row) => row.stock), 1)}
                // Zero reads as a broken row rather than the most urgent one.
                caption={item.stock === 0 ? t('outOfStock') : `${count(item.stock, locale)} ${t('unit')}`}
                tone={item.stock === 0 ? 'danger' : 'warning'}
              />
              </Link>
            ))}
          </div>
        )}
      </Section>
    ),
    // Who to call (ADR-104): at risk now, the biggest spenders first, nobody already following them up.
    customersToCall: () => (
      <Section title={t('customersToCall')} subtitle={t('customersToCallHint')} action={viewAll('/customers?stage=at_risk')} flush>
        {loading && toCall.length === 0 ? (
          <div className="space-y-3 border-t border-line p-5">
            {Array.from({ length: 3 }, (_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : toCall.length === 0 ? (
          <div className="border-t border-line">
            <EmptyState title={t('customersToCallEmpty')} icon={<CheckIcon />} />
          </div>
        ) : (
          <ul className="flex flex-col border-t border-line">
            {toCall.map((customer) => (
              <li key={customer.id} className="border-t border-line first:border-t-0">
                <Link
                  to={`/customers/${customer.id}`}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-3.5 transition-colors hover:bg-surface-sunken"
                >
                  <span className="text-sm font-medium text-ink">{customer.name || customer.email}</span>
                  {customer.phone ? (
                    <span dir="ltr" data-numeric className="text-xs text-ink-muted">
                      {customer.phone}
                    </span>
                  ) : null}
                  <span className="ms-auto text-xs text-ink-muted">
                    {t('lastOrderDaysAgo', { days: count(Math.floor((Date.now() - Date.parse(customer.lastOrderAt)) / 86_400_000), locale) })}
                  </span>
                  <span data-numeric className="shrink-0 text-sm font-medium text-ink">
                    {money(customer.totalSpent, locale)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Section>
    ),
    recentOrders: () => (
      <Section title={t('recentOrders')} action={viewAll('/orders')} flush>
        {loading && recentOrders.length === 0 ? (
          <div className="space-y-3 border-t border-line p-5">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : recentOrders.length === 0 ? (
          <div className="border-t border-line">
            <EmptyState title={t('noOrders')} body={t('noOrdersYet')} icon={<AlertIcon />} />
          </div>
        ) : (
          <div className="overflow-x-auto border-t border-line">
            <Table>
              <thead>
                <tr>
                  <Th>{t('orderNumber')}</Th>
                  <Th>{t('customer')}</Th>
                  <Th>{t('status')}</Th>
                  <Th>{t('paymentColumn')}</Th>
                  <Th align="end">{t('total')}</Th>
                  <Th align="end">{t('placed')}</Th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((order) => (
                  // The whole row opens the order; the number stays a real
                  // link for a middle-click or a copied address.
                  <Tr key={order.id} onClick={() => navigate(`/orders/${order.id}`)}>
                    <Td>
                      <Link
                        to={`/orders/${order.id}`}
                        onClick={(event) => event.stopPropagation()}
                        data-numeric
                        dir="ltr"
                        className="font-medium text-brand-700 hover:underline"
                      >
                        {order.number}
                      </Link>
                    </Td>
                    <Td className="text-ink">{order.shippingAddress.fullName}</Td>
                    <Td>
                      <Pill tone={statusTone(order.status)}>{statusLabel(order.status, order.isPickup)}</Pill>
                    </Td>
                    <Td className="whitespace-nowrap text-ink-muted">
                      {order.paymentMethod === 'COD' ? t('paymentCod') : t('paymentCard')}
                    </Td>
                    <Td align="end" data-numeric className="font-medium text-ink">
                      {money(order.totals.grandTotal, locale, order.totals.currency)}
                    </Td>
                    <Td align="end" className="whitespace-nowrap text-ink-muted">
                      {age(order.placedAt ?? order.createdAt, locale)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Section>
    ),
    visits: () => <VisitsSection visits={visits} loading={loading} refreshing={refreshing} periodLabel={periodLabel} />,
    myFollowUps: () => <MyFollowUpsSection followUps={myFollowUps} loading={loading} />,
    // ADR-121: what to do next, the month against its goal, and right now.
    insights: () => <InsightsSection />,
    target: () => <TargetSection />,
    live: () => <LiveSection />,
  };

  // Greeted by name, with the day — the overview is the first thing someone opens.
  const now = new Date();
  const firstName = user?.name.trim().split(/\s+/)[0] ?? '';
  const greeting = firstName ? t(GREETINGS[partOfDay(now.getHours())], { name: firstName }) : t('overview');

  return (
    <Page
      title={greeting}
      subtitle={`${longDay(now, locale)} · ${t('overviewSubtitle')}`}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Segmented
            label={t('overviewPeriod')}
            value={String(days)}
            onChange={(value) => void load(Number(value) as OverviewPeriod)}
            items={PERIODS.map((period) => ({ id: String(period.days), label: t(period.label) }))}
          />
          <Button variant="secondary" size="sm" onClick={() => setCustomizing(true)}>
            <SlidersIcon className="size-4" />
            {t('customize')}
          </Button>
        </div>
      }
    >
      {error ? <Alert>{error}</Alert> : null}

      {rows.map((row) =>
        row.length === 1 ? (
          <Fragment key={row[0]}>{render[row[0]!]()}</Fragment>
        ) : (
          // Two sections share the row; the grid's rows share their height, so the panels' edges line up.
          <div
            key={row.join('+')}
            // One shrinkable column on a phone: an auto column would grow to the chart's drawn width.
            className="grid grid-cols-1 gap-5 xl:grid-cols-[var(--overview-columns)]"
            style={{ '--overview-columns': columnsOf(row) } as CSSProperties}
          >
            {render[row[0]!]()}
            {render[row[1]!]()}
          </div>
        ),
      )}

      {customizing ? (
        <DashboardCustomize
          onClose={() => setCustomizing(false)}
          // A section just shown may need its data.
          onSaved={() => void load()}
        />
      ) : null}
    </Page>
  );
}

/** The funnel in brief (ADR-108/109): how many came, and how far they went — each step against all visits. */
function VisitsSection({
  visits,
  loading,
  refreshing,
  periodLabel,
}: {
  visits: FunnelReportDto | null;
  loading: boolean;
  refreshing: boolean;
  periodLabel: string;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const steps: { key: MessageKey; value: number }[] = visits
    ? [
        { key: 'funnelVisits', value: visits.visits },
        { key: 'funnelViewed', value: visits.viewedProduct },
        { key: 'funnelAdded', value: visits.addedToCart },
        { key: 'funnelCheckout', value: visits.beganCheckout },
        { key: 'funnelOrdered', value: visits.ordered },
      ]
    : [];

  return (
    <Section
      title={t('overviewVisits')}
      subtitle={periodLabel}
      action={
        <ButtonLink to="/reports" variant="ghost" size="sm">
          {t('viewAll')}
        </ButtonLink>
      }
    >
      {loading && !visits ? (
        <div className="space-y-4">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-8" />
          ))}
        </div>
      ) : !visits || visits.visits === 0 ? (
        <EmptyState title={t('reportFunnelNone')} icon={<ChartIcon />} />
      ) : (
        <ol className={cx('flex flex-col gap-4 transition-opacity', refreshing && 'opacity-50')}>
          {steps.map((step) => (
            <li key={step.key} className="flex flex-col gap-1.5">
              <div className="flex items-baseline justify-between gap-3">
                <span className="truncate text-sm text-ink">{t(step.key)}</span>
                <span data-numeric className="shrink-0 text-sm font-medium text-ink">
                  {count(step.value, locale)}
                  {step.key === 'funnelVisits' ? null : (
                    <span className="ms-2 text-xs font-normal text-ink-muted">{share(step.value, visits.visits, locale)}</span>
                  )}
                </span>
              </div>
              <span className="h-1.5 overflow-hidden rounded-full bg-surface-sunken">
                <span
                  className="block h-full rounded-full"
                  style={{ width: `${step.value === 0 ? 0 : Math.max(2, (step.value / visits.visits) * 100)}%`, backgroundColor: 'var(--chart-1)' }}
                />
              </span>
            </li>
          ))}
        </ol>
      )}
    </Section>
  );
}

/** My open follow-ups (ADR-104/109), the most urgent first — the bell's count, with names. */
function MyFollowUpsSection({ followUps, loading }: { followUps: FollowUpDto[]; loading: boolean }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);

  return (
    <Section
      title={t('myFollowUps')}
      subtitle={t('myFollowUpsHint')}
      action={
        <ButtonLink to="/follow-ups" variant="ghost" size="sm">
          {t('viewAll')}
        </ButtonLink>
      }
      flush
    >
      {loading && followUps.length === 0 ? (
        <div className="space-y-3 border-t border-line p-5">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-10" />
          ))}
        </div>
      ) : followUps.length === 0 ? (
        <div className="border-t border-line">
          <EmptyState title={t('myFollowUpsEmpty')} icon={<CheckIcon />} />
        </div>
      ) : (
        <ul className="flex flex-col border-t border-line">
          {followUps.map((followUp) => (
            <li key={followUp.id} className="border-t border-line first:border-t-0">
              <Link
                to={followUp.customer ? `/customers/${followUp.customer.id}` : followUp.order ? `/orders/${followUp.order.id}` : '/follow-ups'}
                className="flex flex-col gap-1 px-5 py-3.5 transition-colors hover:bg-surface-sunken"
              >
                <span className="flex items-center gap-2">
                  <span dir="auto" className="min-w-0 truncate text-sm font-medium text-ink">
                    {followUp.title}
                  </span>
                  {followUp.overdue ? <Pill tone="danger">{t('followUpOverdue')}</Pill> : null}
                </span>
                <span className="flex flex-wrap items-center gap-x-2 text-xs text-ink-muted">
                  {[followUp.customer?.name, followUp.order?.number].filter(Boolean).join(' · ')}
                  <span className="ms-auto">{dateTime(followUp.dueAt, locale)}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
